export const DEFAULT_REFERRAL_FORM = {
  nom_parrain: "",
  prenom_parrain: "",
  club_parrain: "",
  email_parrain: "",
  telephone_parrain: "",
  club_parraine: "",
  nom_contact_parraine: "",
  prenom_contact_parraine: "",
  email_parraine: "",
  telephone_parraine: "",
};

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function parseDateSafe(value) {
  if (!value) return null;

  const date = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? new Date(`${value}T00:00:00`)
    : new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

export function dayAnchor(date) {
  const copy = new Date(date);
  copy.setHours(12, 0, 0, 0);
  return copy;
}

export function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function formatDateFR(value) {
  const date = parseDateSafe(value);

  if (!date) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

export function moneyToNumber(value) {
  if (typeof value === "number") return value;
  if (value === null || value === undefined) return NaN;

  let text = String(value).trim();

  if (!text) return NaN;

  text = text.replace(/\s/g, "").replace(/€|eur/gi, "");
  text = text.replace(/[^0-9,.\-]/g, "");

  if (text.includes(",") && text.includes(".")) {
    text = text.replace(/\./g, "").replace(",", ".");
  } else if (text.includes(",") && !text.includes(".")) {
    text = text.replace(",", ".");
  }

  const number = Number(text);

  return Number.isFinite(number) ? number : NaN;
}

export function formatEuro(value) {
  const number = moneyToNumber(value);

  if (!Number.isFinite(number)) return "—";

  return number.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function isReferralSubscription(subscription) {
  const amount = moneyToNumber(subscription?.total_amount);
  return Number.isFinite(amount) && amount < 0;
}

export function isActiveStatus(status) {
  const key = normalizeKey(status);

  return (
    key === "active" ||
    key === "abonne" ||
    key.includes("abonn") ||
    key === "subscribed" ||
    key.includes("cours")
  );
}

export function getStatusMeta(status) {
  const raw = String(status || "").trim();
  const key = normalizeKey(raw);

  if (!key) {
    return {
      label: "—",
      badge:
        "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
      row: "hover:bg-slate-50 dark:hover:bg-slate-800/50",
    };
  }

  if (
    key === "active" ||
    key === "abonne" ||
    key.includes("abonn") ||
    key === "subscribed"
  ) {
    return {
      label: "Abonné",
      badge:
        "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
      row:
        "bg-emerald-50/40 hover:bg-emerald-100/50 dark:bg-emerald-500/5 dark:hover:bg-emerald-500/10",
    };
  }

  if (key === "pending" || key.includes("attente")) {
    return {
      label: "En attente",
      badge:
        "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
      row:
        "bg-amber-50/40 hover:bg-amber-100/50 dark:bg-amber-500/5 dark:hover:bg-amber-500/10",
    };
  }

  if (
    key === "expired" ||
    key.includes("expire") ||
    key.includes("termine") ||
    key.includes("annule") ||
    key === "cancelled" ||
    key === "canceled"
  ) {
    return {
      label: "Terminé",
      badge:
        "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
      row:
        "bg-rose-50/40 hover:bg-rose-100/50 dark:bg-rose-500/5 dark:hover:bg-rose-500/10",
    };
  }

  if (key.includes("pause")) {
    return {
      label: "En pause",
      badge:
        "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
      row:
        "bg-violet-50/40 hover:bg-violet-100/50 dark:bg-violet-500/5 dark:hover:bg-violet-500/10",
    };
  }

  if (key.includes("progress") || key.includes("cours")) {
    return {
      label: "En cours",
      badge:
        "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20",
      row:
        "bg-sky-50/40 hover:bg-sky-100/50 dark:bg-sky-500/5 dark:hover:bg-sky-500/10",
    };
  }

  return {
    label: raw || "—",
    badge:
      "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
    row: "hover:bg-slate-50 dark:hover:bg-slate-800/50",
  };
}

export function getPeriodicityMeta(periodicity) {
  const key = normalizeKey(periodicity);

  if (key === "yearly" || key === "annual" || key.includes("annuel")) {
    return {
      label: "Annuel",
      badge:
        "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20",
    };
  }

  if (key === "monthly" || key.includes("mensuel")) {
    return {
      label: "Mensuel",
      badge:
        "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20",
    };
  }

  if (
    key === "trimestrial" ||
    key === "quarterly" ||
    key.includes("trimestriel")
  ) {
    return {
      label: "Trimestriel",
      badge:
        "bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-500/20",
    };
  }

  return {
    label: periodicity || "—",
    badge:
      "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
  };
}

export function getReferralMeta() {
  return {
    label: "Parrainage",
    badge:
      "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
  };
}

export function computeProgress(startDate, endDate) {
  const startRaw = parseDateSafe(startDate);
  const endRaw = parseDateSafe(endDate);

  if (!startRaw || !endRaw) {
    return {
      valid: false,
      percent: 0,
      daysRemaining: null,
      text: "—",
      barClass: "bg-slate-400",
    };
  }

  const today = dayAnchor(new Date());
  const start = dayAnchor(startRaw);
  const end = dayAnchor(endRaw);

  const totalMs = end.getTime() - start.getTime();
  const daysRemaining = Math.ceil((end.getTime() - today.getTime()) / 86400000);

  let percent = 0;

  if (totalMs <= 0) {
    percent = today.getTime() >= end.getTime() ? 100 : 0;
  } else if (today.getTime() <= start.getTime()) {
    percent = 0;
  } else if (today.getTime() >= end.getTime()) {
    percent = 100;
  } else {
    percent = ((today.getTime() - start.getTime()) / totalMs) * 100;
  }

  percent = clamp(Math.round(percent), 0, 100);

  const expired = today.getTime() > end.getTime();
  const notStarted = today.getTime() < start.getTime();

  let barClass = "bg-emerald-600";

  if (expired) barClass = "bg-rose-600";
  else if (daysRemaining <= 30) barClass = "bg-amber-500";

  let text = `${percent}%`;

  if (expired) text = `Expiré • ${percent}%`;
  else if (notStarted) text = `Démarre bientôt • ${percent}%`;
  else text = `J-${Math.max(0, daysRemaining)} • ${percent}%`;

  return {
    valid: true,
    percent,
    daysRemaining,
    expired,
    notStarted,
    text,
    barClass,
  };
}

export function sortSubscriptions(rows) {
  return safeArray(rows).slice().sort((a, b) => {
    const aStart = parseDateSafe(a?.start_date)?.getTime() || 0;
    const bStart = parseDateSafe(b?.start_date)?.getTime() || 0;

    return bStart - aStart;
  });
}

export function splitSubscriptions(rows) {
  const subscriptions = [];
  const referrals = [];

  for (const row of safeArray(rows)) {
    if (isReferralSubscription(row)) referrals.push(row);
    else subscriptions.push(row);
  }

  return {
    subscriptions: sortSubscriptions(subscriptions),
    referrals: sortSubscriptions(referrals),
  };
}

export function calculateSubscriptionStats(rows) {
  const list = safeArray(rows);
  const { subscriptions, referrals } = splitSubscriptions(list);

  const subscriptionAmount = subscriptions.reduce(
    (sum, item) => sum + (moneyToNumber(item?.total_amount) || 0),
    0
  );

  const referralAmount = referrals.reduce(
    (sum, item) => sum + (moneyToNumber(item?.total_amount) || 0),
    0
  );

  const netAmount = subscriptionAmount + referralAmount;

  const activeSubscriptions = subscriptions.filter((item) =>
    isActiveStatus(item?.status)
  );

  const today = dayAnchor(new Date());

  const closestActive = activeSubscriptions
    .map((item) => {
      const end = parseDateSafe(item?.end_date);
      if (!end) return null;

      const daysRemaining = Math.ceil(
        (dayAnchor(end).getTime() - today.getTime()) / 86400000
      );

      return {
        item,
        end,
        daysRemaining,
      };
    })
    .filter(Boolean)
    .filter((item) => item.daysRemaining >= 0)
    .sort((a, b) => a.daysRemaining - b.daysRemaining)[0];

  const expiredCount = subscriptions.filter((item) => {
    const end = parseDateSafe(item?.end_date);
    if (!end) return false;
    return dayAnchor(end).getTime() < today.getTime();
  }).length;

  return {
    total: list.length,
    subscriptionCount: subscriptions.length,
    referralCount: referrals.length,
    activeCount: activeSubscriptions.length,
    expiredCount,
    subscriptionAmount,
    referralAmount,
    netAmount,
    closestEndDate: closestActive?.end || null,
    closestDaysRemaining:
      typeof closestActive?.daysRemaining === "number"
        ? closestActive.daysRemaining
        : null,
  };
}

export function validateEmail(value) {
  if (!value) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());
}

export function validateReferralForm(formData) {
  const errors = {};

  if (!String(formData.club_parrain || "").trim()) {
    errors.club_parrain = "Le club parrain est requis.";
  }

  if (!String(formData.nom_parrain || "").trim()) {
    errors.nom_parrain = "Le nom du parrain est requis.";
  }

  if (!String(formData.prenom_parrain || "").trim()) {
    errors.prenom_parrain = "Le prénom du parrain est requis.";
  }

  if (!String(formData.email_parrain || "").trim()) {
    errors.email_parrain = "L’email du parrain est requis.";
  } else if (!validateEmail(formData.email_parrain)) {
    errors.email_parrain = "L’email du parrain est invalide.";
  }

  if (!String(formData.club_parraine || "").trim()) {
    errors.club_parraine = "Le club parrainé est requis.";
  }

  if (formData.email_parraine && !validateEmail(formData.email_parraine)) {
    errors.email_parraine = "L’email du club parrainé est invalide.";
  }

  return errors;
}